import { useState, useCallback, useEffect } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Loader, Tip, Toast } from '@vzmi/shp_shared_compo'
import { FetchDataUtil, ExcelExport, ExcelExportHelper } from '@vzmi/component_ec_nodejs_util'
import { HistoryUtil } from '@vzmi/component_ec_nodejs_util/dist/lib'
import { getDefaultTimezoneMoment, numeralFormat, dollarFormat } from '../../../../../lib/renderUtil'
import { DEFAULT_NO_FOUND_DESCRIPTION, EMPTY_VALUE } from '../../../../constants/globalTypes'
import { DATE_FORMAT } from '../../../../lib/MomentHelper'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import { fetchSePaginationBestsellers } from '../../../../actions'
import PageLayout from '..'
import Section from '../../../Common/ItemPage/Section/Section'
import TableAction from '../../../Common/NewDashboard/Table/TableAction/TableAction'
import Sort from '../../../Common/NewDashboard/Sort/Sort'
import RankingTable, {
  FIELD as TABLE_FIELD,
  FIELD_MAP as TABLE_FIELD_MAP
} from '../../Dashboard/SalesPerformance/RankingTable/RankingTable'
import {
  SORT_OPTIONS
} from '../../Dashboard/SalesPerformance'
import { RANKING_SIZE } from '../../Dashboard/SalesPerformance/constants'
import { getInitialTimeRange, transformSellersRankings } from '../../Dashboard/SalesPerformance/helper'
import { IconExportButton } from '../../../Common/ExportButton'
import styles from './index.css'

export const PER_PAGE_SIZE = 20
export const EXPORT_MAX_SIZE = 1000

const EXCEL_COLUMNS = [
  TABLE_FIELD.LISTING_ID,
  TABLE_FIELD.NAME,
  TABLE_FIELD.SOLD_AMOUNT,
  TABLE_FIELD.SOLD_QUANTITY,
  TABLE_FIELD.PAGE_VIEW,
  TABLE_FIELD.CONVERSION_RATE,
  TABLE_FIELD.UNIQUE_USER,
  TABLE_FIELD.AVAILABLE_QUANTITY,
  TABLE_FIELD.PRICE
]

const ExportHelper = new ExcelExportHelper({
  headers: {
    ranking: '排名',
    ...(EXCEL_COLUMNS.reduce((map, field) => {
      map[field] = TABLE_FIELD_MAP[field]
      return map
    }, {}))
  },
  fileName: 'seller_TopSellingItems_{{ nowDate.format("YYYYMMDDHHmmss") }}',
  bookType: 'xlsx'
})

const getCurrentOffset = page => PER_PAGE_SIZE * (page - 1)

const getParams = state => {
  const { currentPage, gdids, rankingSortType } = state
  const offset = getCurrentOffset(currentPage)

  const { reportDateStart, reportDateEnd } = getInitialTimeRange()

  const params = {
    sort: `-${rankingSortType}`,
    offset,
    limit: PER_PAGE_SIZE,
    reportDate: `${reportDateStart}/${reportDateEnd}`,
    realTime: true
  }

  if (gdids) {
    params.listingIds = gdids
  }

  return params
}

const getFormattedConversionRate = (item) => {
  const pageView = _.get(item, 'pageView', 0)
  const conversionRate = _.get(item, 'conversionRate')

  if (pageView === 0 || _.isNil(conversionRate)) {
    return EMPTY_VALUE
  }
  return `${(conversionRate * 100).toFixed(2)}%`
}

const getSheetNameFromRange = (range) => {
  if (_.isEmpty(range)) {
    return 'Sheet1'
  }
  const [start, end] = range
  const startFormatted = moment(start).format('MMDDHHmm')
  const endFormatted = moment(end).format('MMDDHHmm')
  return `${startFormatted}-${endFormatted}`
}

const getSheetDataFromBestsellers = (bestsellers) => {
  return _.map(bestsellers, item => {
    return {
      ...item,
      soldAmount: dollarFormat(_.get(item, 'soldAmount', 0)),
      soldQuantity: numeralFormat(_.get(item, 'soldQuantity', 0), { allowZero: true }),
      pageView: numeralFormat(_.get(item, 'pageView', 0), { allowZero: true }),
      conversionRate: getFormattedConversionRate(item),
      uniqueUser: numeralFormat(_.get(item, 'uniqueUser', 0), { allowZero: true }),
      availableQuantity: numeralFormat(_.get(item, 'availableQuantity', 0), { allowZero: true }),
      price: dollarFormat(_.get(item, 'price', 0))
    }
  })
}

const useItemSalesPerformance = (actions) => {
  const [loading, setLoading] = useState(false)
  const [itemData, setItemData] = useState(null)

  const fetchData = useCallback(async (state) => {
    setLoading(true)

    const apiParams = getParams(state)
    const [error, payload] = await safeAwait(
      actions.fetchSePaginationBestsellers(apiParams).then(httpHandler)
    )

    if (error) {
      const errorMessage = errorHelper.parseErrorMessage(error)
      Toast.error(errorMessage)
      setLoading(false)
      return
    }

    const {
      bestsellers = [],
      pagination = {},
      reportDateStart,
      reportDateEnd
    } = payload

    const resultsTotal = _.get(pagination, 'resultsTotal', 0)
    const range = [reportDateStart, reportDateEnd]
    const convertedSellersRankings = transformSellersRankings(bestsellers, PER_PAGE_SIZE)

    const paginationProps = {
      total: resultsTotal,
      current: state.currentPage,
      pageSize: PER_PAGE_SIZE
    }

    setItemData({
      sellersRankings: convertedSellersRankings,
      paginationProps,
      range,
      resultsTotal,
      startIndex: getCurrentOffset(state.currentPage)
    })

    setLoading(false)
  }, [actions])

  return {
    loading,
    itemData,
    fetchData
  }
}

const getTopSellingItems = async (actions, state) => {
  const { rankingSortType, gdids } = state

  const { reportDateStart, reportDateEnd } = getInitialTimeRange()

  const params = {
    sort: `-${rankingSortType}`,
    reportDate: `${reportDateStart}/${reportDateEnd}`,
    maxResults: EXPORT_MAX_SIZE,
    realTime: true
  }

  if (gdids) {
    params.listingIds = gdids
  }

  const [error, payload] = await safeAwait(
    FetchDataUtil.fetchAllPages(params => {
      return actions.fetchSePaginationBestsellers(params)
        .then(res => {
          if (res.error) {
            throw res
          }
          return res
        })
    }, {
      payload: params,
      selector: res => _.get(res, 'bestsellers', [])
    })
  )

  if (error) {
    return [error]
  }

  return [undefined, payload]
}

const SalesPerformance = (props) => {
  const { actions } = props
  const { gdids = '' } = HistoryUtil.retrieveQueryParams()

  const [currentPage, setCurrentPage] = useState(1)
  const [rankingSortType, setRankingSortType] = useState(SORT_OPTIONS[0].value)

  const {
    loading,
    itemData,
    fetchData
  } = useItemSalesPerformance(actions)

  const {
    sellersRankings = [],
    paginationProps = {},
    range = [],
    resultsTotal = 0,
    startIndex = 0
  } = itemData || {}

  useEffect(() => {
    fetchData({ currentPage, gdids, rankingSortType })
  }, [fetchData, currentPage, gdids, rankingSortType])

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])

  const handleRankingSortChange = useCallback((event) => {
    const newSortType = _.get(event, 'target.value', '')
    if (newSortType === rankingSortType) {
      return
    }
    setRankingSortType(newSortType)
    setCurrentPage(1)
  }, [rankingSortType])

  const handleExportClick = useCallback(async () => {
    const [error, payload] = await getTopSellingItems(actions, {
      rankingSortType,
      gdids
    })

    if (error) {
      Toast.error('下載失敗，請稍後再試')
      return
    }

    const bestsellers = _.get(payload, 'data', [])
    const convertedRankings = transformSellersRankings(bestsellers, EXPORT_MAX_SIZE)
    const sheetData = getSheetDataFromBestsellers(convertedRankings)

    const option = {
      fileName: ExportHelper.getFileName({
        nowDate: getDefaultTimezoneMoment()
      }),
      sheets: [
        {
          name: getSheetNameFromRange(range),
          headers: ExportHelper.getSheetHeaders(),
          data: ExportHelper.convertDataByHeaders(sheetData)
        }
      ]
    }

    const ExcelExporter = new ExcelExport(option)
    ExcelExporter.download({ bookType: ExportHelper.getBookType() })
  }, [actions, rankingSortType, gdids, range])

  const rankingColumnProps = {
    rankingSize: RANKING_SIZE,
    width: 35,
    startIndex
  }

  const disableExportBtn = loading || resultsTotal === 0

  const getDateRangeText = () => {
    if (_.isEmpty(range)) {
      return `資料統計區間：${EMPTY_VALUE} ~ ${EMPTY_VALUE}`
    }

    const dateRange = _.map(range, datetime => {
      return moment(datetime).format(DATE_FORMAT.SCHEDULE_SECONDS)
    })
    const [start, end] = dateRange
    return `資料統計區間：${start} ~ ${end}`
  }

  return (
    <PageLayout>
      <Helmet>
        <title>即時業績-賣場排行</title>
      </Helmet>
      <div className={styles.salesPerformance}>
        <Section
          title={(
            <div>
              即時業績-賣場排行
              <IconExportButton
                disabled={disableExportBtn}
                data-role='export-button'
                className={styles.exportBtn}
                onClick={handleExportClick}
                label='下載整筆檔案'
              />
              <Tip
                type='info'
                className={styles.tips}
              >
                最多下載前 1000 筆
              </Tip>
            </div>
          )}
        >
          <Loader loaded={!loading}>
            <p className={styles.dateRange}>
              {getDateRangeText()}
            </p>
            <TableAction
              className={styles.tableAction}
              leftContent={(
                <Tip
                  type='info'
                  className={styles.tips}
                >
                  {`共${resultsTotal}筆，每頁${PER_PAGE_SIZE}筆`}
                </Tip>
              )}
            >
              <Sort
                id='sales-performance-list'
                options={SORT_OPTIONS}
                onChange={handleRankingSortChange}
                sortType={rankingSortType}
              />
            </TableAction>
            <RankingTable
              data={sellersRankings}
              rankingColumnProps={rankingColumnProps}
              noFoundDescription={DEFAULT_NO_FOUND_DESCRIPTION}
              paginationProps={{
                ...paginationProps,
                onChange: handlePageChange
              }}
              showPagination
            />
          </Loader>
        </Section>
      </div>
    </PageLayout>
  )
}

const mapDispatchToProps = (dispatch) => {
  const actions = {
    fetchSePaginationBestsellers
  }
  return {
    actions: bindActionCreators(actions, dispatch)
  }
}

export default connect(null, mapDispatchToProps)(SalesPerformance)
